import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { TextMedium, TextRegular } from 'UiKit/typography';
import InputSelect from 'UiKit/input-select';
import InputDatepicker from 'UiKit/input-datepicker';
import InputComponent from 'UiKit/input';
import ButtonSolid from 'UiKit/button/buttonSolid';
import useGetGenerateSchedule from 'Domain/schedule-generate/useGetGenerateSchedule';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetRulesets from 'Domain/schedule-generate/useGetRulesets';
import useGetRulesetDetail from 'Domain/schedule-generate/useGetRulesetDetail';
import { Condition } from 'Component/others';

import InputPeakDay from 'View/schedule-generate/component/input-peak-day';
import OnLeaveHosts from 'View/schedule-generate/component/on-leave-hosts';
import RulesetDetail from 'View/schedule-generate/component/ruleset-detail';
import InputRuleset from 'View/schedule-generate/component/input-ruleset';
import getPeriodOptions from 'View/schedule-generate/helper/getPeriodOptions';

import styles from './styles.module.css';
import './datepicker-override.scss';
import checkIsDisabledSubmitForm from 'View/schedule-generate/helper/checkIsDisabledSubmitForm';

const Form = ({ form }) => {
  const { t } = useTranslation('schedule-generate');
  const { setValue, watch } = form;
  const values = watch();
  const { brand, yearMonth, rulesetId, totalDaysOffPerHost, isEventDay, customDoubleDates, peakDays, isAnyHostLeave, hostLeaves } = values;
  const [startDoubleDates, endDoubleDates] = customDoubleDates;

  const { mutate } = useGetGenerateSchedule();

  const { data: brandOptions } = useGetBrand();
  const { data: rulesetOptions, isLoading: isLoadingRulesets } = useGetRulesets({ yearMonth, brand });
  const { data: rulesetDetail } = useGetRulesetDetail({ rulesetId });

  const isDisabled = checkIsDisabledSubmitForm(values);

  const periodOptions = getPeriodOptions();
  const selectedPeriod = periodOptions.find((p) => p.value === yearMonth) || null;

  const [selYear, selMonth] = yearMonth ? yearMonth.split('-').map(Number) : [null, null];
  const minDate = selYear != null ? new Date(selYear, selMonth - 1, 16) : null;
  const maxDate = selYear != null ? new Date(selYear, selMonth, 15) : null;

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  return (
    <Box className={styles['form__container']}>
      <Box className={styles['form__section--head']}>
        <TextMedium className={styles['form__text--subtitle']}>{t('fillInFieldsBelow')}</TextMedium>
      </Box>
      <Box className={styles['form__section--content']}>
        <Box className={styles['form__section--fields']}>
          <Flex width={'100%'} gap={'24px'}>
            <Box className={styles['form__input--item']}>
              <InputSelect
                label={t('fieldBrand')}
                placeholder={t('fieldBrandPlaceholder')}
                options={brandOptions}
                value={brand}
                onChange={(brand) => {
                  setValue('brand', brand);
                  setValue('rulesetId', null);
                }}
                minHeight="34px"
                titleClassName={styles['form__text--label']}
              />
            </Box>
            <Box className={styles['form__input--item']}>
              <InputSelect
                label={t('scheduleForMonth')}
                placeholder={t('fieldYearMonthPlaceholder')}
                options={periodOptions}
                value={selectedPeriod}
                onChange={(option) => {
                  setValue('yearMonth', option?.value ?? null);
                  setValue('rulesetId', null);
                  setValue('customDoubleDates', [null, null]);
                  setValue('isEventDay', false);
                  setValue('peakDays', []);
                }}
                minHeight="34px"
                titleClassName={styles['form__text--label']}
                isSearchable={false}
              />
            </Box>
          </Flex>
          <Condition statement={yearMonth && brand}>
            <Box className={styles['form__input--item']}>
              <InputRuleset
                label={t('ruleset')}
                placeholder={t('fieldRulesetPlaceholder')}
                options={rulesetOptions}
                onChange={(rulesetId) => {
                  setValue('rulesetId', rulesetId);
                  setValue('isAnyHostLeave', false);
                  setValue('hostLeaves', []);
                }}
                value={rulesetId}
                minHeight="34px"
                titleClassName={styles['form__text--label']}
                isLoading={isLoadingRulesets}
              />
            </Box>
            <Flex width={'100%'} gap={'24px'}>
              <Box className={styles['form__input--item']}>
                <InputComponent
                  label={t('totalTimeOffPerHost')}
                  value={totalDaysOffPerHost}
                  onChange={(totalDaysOffPerHost) => {
                    const number = String(totalDaysOffPerHost).replace(/[^0-9]/g, '');
                    setValue('totalDaysOffPerHost', number);
                  }}
                  rightButton={<TextRegular className={styles['form__text--offday']}>{t('dayText')}</TextRegular>}
                  height="34px"
                />
              </Box>
              <Box className={styles['form__input--item']}>
                <InputDatepicker
                  placeholder={t('fieldDoubleDatePlaceholder')}
                  label={t('doubleDate')}
                  minDate={minDate}
                  maxDate={startDoubleDates ? addDays(new Date(startDoubleDates), 2) : maxDate}
                  startDate={startDoubleDates}
                  endDate={endDoubleDates}
                  selectsRange={true}
                  dateFormat="dd MMM"
                  onChange={(e) => setValue('customDoubleDates', e)}
                  shouldCloseOnSelect={true}
                  titleClassname={styles['form__text--label']}
                  className={'form__wrapper--datepicker'}
                  datepickerClassname={styles['form__container--datepicker']}
                />
              </Box>
            </Flex>
            <InputPeakDay isEventDay={isEventDay} setValue={setValue} peakDays={peakDays} maxDate={maxDate} minDate={minDate} />
            <OnLeaveHosts
              isAnyHostLeave={isAnyHostLeave}
              setValue={setValue}
              rulesetDetail={rulesetDetail}
              hostLeaves={hostLeaves}
              maxDate={maxDate}
              minDate={minDate}
              customDoubleDates={customDoubleDates}
              peakDays={peakDays}
            />
          </Condition>
        </Box>
        <Condition statement={rulesetId && rulesetDetail}>
          <RulesetDetail rulesetDetail={rulesetDetail} />
        </Condition>
      </Box>
      <Box flexDirection={'column'} alignItems={'center'} gap={'12px'} marginTop={'24px'} className={styles['form__container--footer']}>
        <Condition statement={!isDisabled}>
          <TextMedium>{t('ensureDataCorrect')}</TextMedium>
        </Condition>
        <ButtonSolid onClick={() => mutate(values)} disabled={isDisabled} className={styles['form__button--generate']}>
          {t('title')}
        </ButtonSolid>
      </Box>
    </Box>
  );
};

export default Form;
